import type { DocumentNode, OperationDefinitionNode } from "graphql"
import { print } from "graphql"

import { retryOnlyServerErrors } from "./apiErrors"

type MaybeVariables = Record<string, unknown> | undefined

type AppSignalSpan = {
  setAction: (action: string) => void
  setNamespace: (namespace: string) => void
  setTags: (tags: Record<string, string>) => void
}

type AppSignalClient = {
  addBreadcrumb?: (breadcrumb: { category: string; action: string }) => void
  sendError?: (error: Error, callback?: (span: AppSignalSpan) => void) => void
}

type WindowWithAppSignal = Window & {
  appsignal?: AppSignalClient
}

type GraphQLErrorResponse = {
  message: string
  locations?: Array<{ line: number; column: number }>
  path?: Array<string | number>
  extensions?: Record<string, unknown>
}

type GraphQLResponse<TData> = {
  data?: TData
  errors?: Array<GraphQLErrorResponse>
}

export type GraphQLDocument<TData, TVariables> = DocumentNode & {
  __apiType?: (variables: TVariables) => TData
}

export type GraphQLRequestOptions<TData, TVariables> = {
  document: GraphQLDocument<TData, TVariables> | string
  variables?: TVariables
  operationName?: string
  signal?: AbortSignal
}

const AUTH_ERROR_PATTERNS = [
  /request could not be authenticated/i,
  /authentication failed/i,
  /unauthorized/i,
  /received status code 401/i,
]

const isAuthError = (message: string) => {
  return AUTH_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

export class GraphQLResponseError extends Error {
  body: GraphQLResponse<unknown> | null
  errors: Array<GraphQLErrorResponse>
  response: Pick<Response, "status" | "statusText">

  constructor(
    message: string,
    response: Pick<Response, "status" | "statusText">,
    body: GraphQLResponse<unknown> | null = null,
  ) {
    super(message)
    this.name = "GraphQLResponseError"
    this.response = response
    this.body = body
    this.errors = body?.errors ?? []
  }
}

export const isGraphQLResponseError = (error: unknown): error is GraphQLResponseError => {
  return error instanceof GraphQLResponseError
}

const appsignal = () => (window as WindowWithAppSignal).appsignal

const printedDocuments = new WeakMap<DocumentNode, string>()

const documentToString = <TData, TVariables>(
  document: GraphQLDocument<TData, TVariables> | string,
) => {
  if (typeof document === "string") {
    return document
  }

  let source = printedDocuments.get(document)
  if (!source) {
    source = print(document)
    printedDocuments.set(document, source)
  }
  return source
}

const getOperationDefinition = <TData, TVariables>(
  document: GraphQLDocument<TData, TVariables> | string,
): OperationDefinitionNode | undefined => {
  if (typeof document === "string") {
    return undefined
  }

  return document.definitions.find(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === "OperationDefinition",
  )
}

export const getGraphQLOperationName = <TData, TVariables>(
  document: GraphQLDocument<TData, TVariables> | string,
) => {
  return getOperationDefinition(document)?.name?.value
}

const addGraphQLBreadcrumb = (operationName: string | undefined) => {
  if (process.env.NODE_ENV === "test") {
    return
  }

  const action = `GraphQL query: ${operationName ?? "AnonymousOperation"}`

  appsignal()?.addBreadcrumb?.({
    category: "graphql",
    action,
  })
}

const reportGraphQLError = (
  error: Error,
  message: string,
  type: "graphql" | "network",
  status?: number,
) => {
  if (process.env.NODE_ENV === "test") {
    return
  }

  const authError = status === 401 || isAuthError(message)
  const action = authError
    ? "GraphQL Auth Error"
    : `GraphQL ${type === "network" ? "Network " : ""}Error`
  const namespace = authError ? "GraphQL Auth" : `GraphQL${type === "network" ? " Network" : ""}`

  try {
    appsignal()?.sendError?.(error, (span) => {
      span.setAction(action)
      span.setNamespace(namespace)
      span.setTags({ errorType: authError ? "authentication" : type })
    })
  } catch {
    console.warn(message)
  }
}

const parseResponseBody = async <TData>(
  response: Response,
): Promise<GraphQLResponse<TData> | null> => {
  const bodyText = await response.text()

  if (bodyText.length === 0) {
    return null
  }

  try {
    return JSON.parse(bodyText) as GraphQLResponse<TData>
  } catch {
    throw new GraphQLResponseError("Failed to parse GraphQL response", response)
  }
}

export const retryGraphqlServerErrors = (maxAttempts = 3) => {
  const retryServerErrors = retryOnlyServerErrors(maxAttempts)

  return (failureCount: number, error: unknown) => {
    if (isGraphQLResponseError(error) && error.response.status < 500) {
      return false
    }

    return retryServerErrors(failureCount, error)
  }
}

export const graphqlQueryKey = <TData, TVariables>(
  document: GraphQLDocument<TData, TVariables> | string,
  variables?: TVariables,
  operationName?: string,
) => {
  return [
    "graphql",
    documentToString(document),
    operationName ?? getGraphQLOperationName(document),
    variables ?? {},
  ] as const
}

export const graphqlRequest = async <TData, TVariables = MaybeVariables>({
  document,
  variables,
  operationName = getGraphQLOperationName(document),
  signal,
}: GraphQLRequestOptions<TData, TVariables>): Promise<TData> => {
  addGraphQLBreadcrumb(operationName)

  let response: Response

  try {
    response = await fetch("/graphql", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/graphql-response+json, application/json",
        "Content-Type": "application/json",
        "client-name": "AppSignal.com",
      },
      body: JSON.stringify({
        query: documentToString(document),
        variables,
        operationName,
      }),
      signal,
    })
  } catch (error) {
    const networkError = error instanceof Error ? error : new Error(String(error))
    if (signal?.aborted || networkError.name === "AbortError") {
      throw networkError
    }
    reportGraphQLError(networkError, `[GraphQL Network error]: ${networkError.message}`, "network")
    throw networkError
  }

  let body: GraphQLResponse<TData> | null
  try {
    body = await parseResponseBody<TData>(response)
  } catch (error) {
    if (signal?.aborted) {
      throw error
    }
    const parseError = error instanceof Error ? error : new Error(String(error))
    reportGraphQLError(parseError, `[GraphQL Network error]: ${parseError.message}`, "network")
    throw parseError
  }

  if (body?.errors && body.errors.length > 0) {
    const error = new GraphQLResponseError(body.errors[0].message, response, body)

    body.errors.forEach(({ message, locations, path }) => {
      reportGraphQLError(
        error,
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        "graphql",
        response.status,
      )
    })

    throw error
  }

  if (!response.ok || !body || !("data" in body)) {
    const message = response.ok
      ? "GraphQL response did not include data"
      : `${response.status} ${response.statusText}`
    const error = new GraphQLResponseError(message, response, body)
    reportGraphQLError(error, `[GraphQL Network error]: ${message}`, "network", response.status)
    throw error
  }

  return body.data as TData
}
